import {
  BarChart3,
  DollarSign,
  MousePointerClick,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Clock,
  CalendarDays,
  CalendarCheck2,
  ChevronsUp,
  Download,
  FileText,
  BarChart4,
  Settings,
  RefreshCw
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import DashboardApiService from "../../ApiServices/DashboardApiService";
import WelcomeBanner from "../Dashboards/WelcomeBanner";
import SectionDivider from "../Dashboards/SectionDivider";
import NoticeBoard from "../privacypolicies/NoticeBoard";
import PrivacyPolicy from "../privacypolicies/PrivacyPolicy";
import { useOutletContext } from "react-router-dom";
import { Button } from "../Common/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../Common/common";
import { toast } from "react-toastify";
import AdTxtApiService from "../../ApiServices/AdsTxtApiService/AdTxtApiService";
import RevenueCards from "../Cards/RevenueCards";
import KPIGrid from "../Cards/KPIGrid";

export default function Dashboard() {
  const { user, admin: isAdmin } = useOutletContext();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7D");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    async function fetchDashboardMetrics() {
      try {
        if (isAdmin) {
          const data = await DashboardApiService.getMergedDashboard();
          setMetrics(data);
        } else {
          const data = await DashboardApiService.getMergedPublisherDashboard(user.id);
          setMetrics(data);
        }
      } catch (err) {
        console.error("Error loading dashboard metrics", err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardMetrics();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const data = await DashboardApiService.getMergedDashboard();
        setMetrics(data);
      } else {
        const data = await DashboardApiService.getMergedPublisherDashboard(user.id);
        setMetrics(data);
      }
    } catch (err) {
      console.error("Error refreshing dashboard metrics", err);
    } finally {
      setLoading(false);
    }
  };

  const kpis = metrics ? [
    { name: "Revenue", value: `$${metrics.totalRevenue.toFixed(2)}`, icon: DollarSign, color: "text-green-500" },
    { name: "eCPM", value: `$${metrics.avgECPM.toFixed(2)}`, icon: TrendingUp, color: "text-blue-500" },
    { name: "Ad Impressions", value: metrics.totalImpressions.toLocaleString(), icon: BarChart3, color: "text-purple-500" },
    { name: "Ad Requests", value: metrics.totalAdRequests.toLocaleString(), icon: BarChart3, color: "text-indigo-500" },
    { name: "Fill Rate", value: `${metrics.avgFillRate.toFixed(2)}%`, icon: BarChart3, color: "text-orange-500" },
    { name: "Payout", value: `$${metrics.payout.toFixed(2)}%`, icon: TrendingUp, color: "text-teal-500" },
    { name: "Clicks", value: metrics.totalClicks.toLocaleString(), icon: MousePointerClick, color: "text-yellow-500" },
    { name: "CTR (%)", value: `${metrics.avgCTR.toFixed(2)}%`, icon: TrendingUp, color: "text-pink-500" },
  ] : [];

  const iconMap = {
    Clock,
    CalendarDays,
    CalendarCheck2,
    ChevronsUp,
  };

  const handleDownloadTaxCertificate = () => {
    const filePath = '/ADSPACE TAX FORM 2025.pdf';
    const currentYear = new Date().getFullYear();
    const link = document.createElement('a');
    link.href = filePath;
    link.download = `ADSPACE TAX FORM ${currentYear}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Tax form ${currentYear} downloaded!`);
  };

  const handleDownloadAdsTxt = async () => {
    await AdTxtApiService.DownloadAdFile("AdTxt");
  };

  const handleViewDetailedAnalytics = () => {
    setActiveTab("analytics");
  };

  const handleAnalytics = () => {
    window.location.href = "/analytics";
  };

  const filteredRevenueTrend = metrics?.revenueTrend?.filter((_, index) => {
    if (timeRange === "7D") return index >= metrics.revenueTrend.length - 7;
    if (timeRange === "30D") return index >= metrics.revenueTrend.length - 30;
    return true; // All data
  });

  return (
    <div className="space-y-8 mt-6">
      {user && <WelcomeBanner name={user.email.split("@")[0]} />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="relative flex w-full max-w-md bg-gray-100 rounded-lg p-1 shadow-sm">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics">Monitoring</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>


        <TabsContent value="overview" className="space-y-8">
          {/* Revenue Period Cards */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
          >
            {/* <RevenueCards metrics={metrics} /> */}
            {metrics?.revenuePeriods?.map((item, index) => {
              const IconComponent = iconMap[item.icon] || Clock;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm border border-white/20" />
                  <div className="relative p-4 h-full rounded-xl">
                    <div className="flex flex-col h-full">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${item.iconColor} mb-3`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <h3 className="text-xs font-medium text-gray-500 mb-1">{item.title}</h3>
                      <p className="text-xl font-bold text-gray-800 mb-2">${item.amount.toFixed(2)}</p>
                      <div className="mt-auto pt-2 border-t border-gray-100/50">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-medium ${item.trendColor} flex items-center`}>
                            {item.isPositive ? (
                              <ArrowUp className="w-3 h-3 mr-1" />
                            ) : (
                              <ArrowDown className="w-3 h-3 mr-1" />
                            )}
                            {item.change}
                          </span>
                          <span className="text-xs text-gray-400">{item.period}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* KPI Cards */}
          {/* <KPIGrid kpis={kpis} /> */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {kpis.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm border border-white/20" />
                <div className="relative p-4 rounded-xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500">{item.name}</p>
                      <h2 className="mt-1 text-xl font-bold text-gray-800">{item.value}</h2>
                    </div>
                    <div className={`p-2 rounded-lg ${item.color.replace("text", "bg") + "100"} ${item.color}`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color.replace("text", "bg")} rounded-full`}
                        style={{ width: `${Math.random() * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <SectionDivider label="Revenue Analytics" />

          {/* Revenue Chart */}
          <div className="relative bg-white/50 backdrop-blur-sm p-4 rounded-xl border border-white/20 shadow-sm overflow-hidden">
            <div className="relative">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Revenue Trend</h3>
                  <p className="text-xs text-gray-500">Daily revenue performance</p>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant={timeRange === "7D" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTimeRange("7D")}
                  >
                    7D
                  </Button>
                  <Button
                    variant={timeRange === "30D" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTimeRange("30D")}
                  >
                    30D
                  </Button>
                  <Button
                    variant={timeRange === "ALL" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTimeRange("ALL")}
                  >
                    All Time
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
              </div>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={filteredRevenueTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#4f46e5"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>


        </TabsContent>

        <TabsContent value="analytics" className="space-y-8">
          <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl border border-white/20 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Detailed Analytics</h2>
                <p className="text-sm text-gray-500">Deep dive into your performance metrics</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/70 p-4 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-700 mb-4">Revenue by Ad Unit</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.revenueByAdUnit || []}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white/70 p-4 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-700 mb-4">Performance Metrics</h3>
                <div className="space-y-4">
                  {metrics?.performanceMetrics?.map((metric, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${metric.trend === 'up' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm font-medium text-gray-700">{metric.name}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-gray-900 mr-2">{metric.value}</span>
                        <span className={`text-xs ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                          {metric.change}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white/70 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-4">Hourly Performance</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics?.hourlyPerformance || []}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="impressions"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="clicks"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tools" className="space-y-8">
          <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl border border-white/20 shadow-sm">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Publisher Tools</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Tax Certificate Card */}
              <div className="bg-white/70 p-5 rounded-lg border border-gray-200 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-medium text-gray-800">Tax Certificate</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Download your annual tax certificate for reporting purposes.
                </p>
                <Button
                  onClick={handleDownloadTaxCertificate}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Certificate
                </Button>
              </div>

              {/* Ads.txt Management Card */}
              <div className="bg-white/70 p-5 rounded-lg border border-gray-200 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 p-2 rounded-lg mr-3">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-medium text-gray-800">Ads.txt Management</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Download or update your ads.txt file and add this in your website.
                </p>
                <div className="flex space-x-2 mt-auto">
                  <Button
                    onClick={handleDownloadAdsTxt}
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download ads.txt
                  </Button>
                </div>
              </div>

              {/* Analytics Settings Card */}
              <div className="bg-white/70 p-5 rounded-lg border border-gray-200 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <BarChart4 className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-medium text-gray-800">Detailed Analytics</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Configure your analytics preferences and data export settings.
                </p>
                <div className="flex space-x-2 mt-auto">
                  <Button onClick={handleAnalytics} variant="outline">
                    View Full Analytics
                  </Button>
                </div>
              </div>

            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NoticeBoard />
            <PrivacyPolicy />
          </div>
         
        </TabsContent>
      </Tabs>
    </div>
  );
}